
import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { getAllEventStatistics, exportEventStatisticsToCSV } from '@/services/eventStatisticsService';
import { EventStatistics } from '@/lib/types';
import { formatDate } from '@/lib/utils';
import { FileDown } from 'lucide-react';

const EventStatisticsPanel = () => {
  const [statistics, setStatistics] = useState<EventStatistics[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    loadStatistics();
  }, []);

  const loadStatistics = async () => {
    setIsLoading(true);
    try {
      // Fejlesztési környezetben a helyi adatokat használjuk
      if (import.meta.env.MODE === 'development') {
        const localStats = getAllEventStatistics();
        setStatistics(localStats);
      } else {
        // TODO: Implement API call to get all event statistics
        // This would be implemented once the backend API is ready
        toast({
          title: "Figyelmeztetés",
          description: "Az API integráció még nincs megvalósítva. Helyőrző adatok jelennek meg.",
          variant: "default",
        });
        
        // Placeholder data for demonstration
        setStatistics([
          {
            eventId: "1",
            eventCode: "E001",
            viewCount: 120,
            clickCount: 45,
            clickThroughRate: 38,
            lastUpdated: new Date().toISOString()
          },
          {
            eventId: "2",
            eventCode: "E002",
            viewCount: 85,
            clickCount: 20,
            clickThroughRate: 24,
            lastUpdated: new Date().toISOString()
          }
        ]);
      }
    } catch (error) {
      console.error('Error loading event statistics:', error);
      toast({
        title: "Hiba történt!",
        description: "Nem sikerült betölteni az esemény statisztikákat.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleExportCSV = () => {
    try {
      const csvContent = exportEventStatisticsToCSV();
      
      // CSV fájl letöltése
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `esemeny-statisztikak-${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast({
        title: "Sikeres exportálás",
        description: "Az esemény statisztikák exportálása sikeresen megtörtént.",
      });
    } catch (error) {
      console.error('Error exporting event statistics:', error);
      toast({
        title: "Hiba történt!",
        description: "Nem sikerült exportálni az esemény statisztikákat.",
        variant: "destructive",
      });
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <div>
          <CardTitle>Esemény statisztikák</CardTitle>
          <CardDescription>
            Megtekintések és átkattintási arányok az eseményekhez
          </CardDescription>
        </div>
        <Button 
          variant="outline" 
          size="sm" 
          className="ml-auto"
          onClick={handleExportCSV}
        >
          <FileDown className="mr-2 h-4 w-4" />
          Exportálás
        </Button>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="h-32 flex items-center justify-center">
            <p className="text-muted-foreground">Statisztikák betöltése...</p>
          </div>
        ) : statistics.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Esemény kód</TableHead>
                <TableHead>Megtekintések</TableHead>
                <TableHead>Kattintások</TableHead>
                <TableHead>Átkattintási arány</TableHead>
                <TableHead>Utolsó frissítés</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {statistics.map((stat) => (
                <TableRow key={stat.eventId}>
                  <TableCell className="font-medium">{stat.eventCode}</TableCell>
                  <TableCell>{stat.viewCount}</TableCell>
                  <TableCell>{stat.clickCount}</TableCell>
                  <TableCell>{stat.clickThroughRate}%</TableCell>
                  <TableCell>{formatDate(new Date(stat.lastUpdated))}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <div className="h-32 flex items-center justify-center">
            <p className="text-muted-foreground">Nincsenek elérhető statisztikák</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default EventStatisticsPanel;
